import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  BBWTMessageService,
  EventService,
  GoogleAnalyticsService,
  JobsService,
  SupplierInboxJobsService,
  SupplierJobInteractionService,
} from '../../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { JobScreenCommonService } from '../common/job-screen-common.service';
import { SupplierJobScreenJob } from '../../../interfaces/screen-specific/job-screen';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { OrganisationTypeEnum } from '../../../enums/organisation-types';
import { TabType } from '../common/tab-scroller/tab-scroller.component';
import { RouteRecommendationService } from '../../../services/route-recommendation.service';

@Component({
  selector: 'app-supplier-job-screen',
  templateUrl: './supplier-job-screen.component.html',
  styleUrls: ['./supplier-job-screen.component.scss', '../common/job-screen-common.scss'],
})
export class SupplierJobScreenComponent implements OnInit, OnDestroy {
  @Input('organisationId') organisationId!: number;

  organisationTypeId: number = OrganisationTypeEnum.Supplier;

  jobId: number;
  job: SupplierJobScreenJob = null;
  subscriptions: Subscription[] = [];
  rejectJobVisible: boolean;

  tabs: TabType[] = [];
  public activeViewId: string = 'scroll';

  private googleAnalyticsEventCategory: string = 'supplier_jobs_inbox';

  routeInboxEnabled: boolean = false;
  routesContainingJobCount: number = 0;

  constructor(
    public commonService: JobScreenCommonService,
    private jobService: JobsService,
    private route: ActivatedRoute,
    private eventService: EventService,
    private location: Location,
    private googleAnalyticsService: GoogleAnalyticsService,
    private supplierInboxJobsService: SupplierInboxJobsService,
    private supplierJobInteractionService: SupplierJobInteractionService,
    private router: Router,
    private routeRecommendationService: RouteRecommendationService,
    private messageService: BBWTMessageService,
  ) {
    this.initSubscriptions();
  }

  ngOnInit(): void {
    this.jobId = parseInt(this.route.snapshot.paramMap.get('id'));

    this.tabs = [
      { label: 'Job Overview', id: 'jobOverview', type: 'scroll' },
      { label: 'Task Details', id: 'taskDetails', type: 'scroll' },
    ];

    this.loadRouteInboxEnabledFlag();
    this.getRoutesContainingJobCount();

    this.getJob(this.jobId).then(() => {});
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  initSubscriptions(): void {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.handleEvents(event);
      }),
    );
  }

  loadRouteInboxEnabledFlag(): void {
    this.routeRecommendationService.isRouteSearchEnabled().then((res) => {
      this.routeInboxEnabled = res;
    });
  }

  getRoutesContainingJobCount(): void {
    this.routeRecommendationService.getRouteCountByJobId(this.jobId).then((count) => {
      this.routesContainingJobCount = count;
    });
  }

  private handleEvents(event: any) {
    if (event.event == 'job-rejected') {
      if (this.jobId == event.data.jobId) {
        void this.getJob(this.jobId);
      }
    }

    if (event.event == 'refresh-job') {
      void this.getJob(this.jobId);
    }
  }

  async getJob(jobId: number): Promise<void> {
    const job = await this.jobService.getForComponent<SupplierJobScreenJob>(
      jobId,
      'supplier-job-screen',
    );

    if (job == null) {
      this.messageService.ShowError(
        '',
        "We were unable to load the selected job as it isn't currently available",
      );
      await this.router.navigate(['app/supplierinbox']);
    }

    if (job.redirectId) {
      window.location.href = `/app/jobscreen/${job.redirectId}`;
    }

    this.job = job;

    this.syncJobDetailsTab();
    this.syncItineraryTab();
  }

  goBack(): void {
    this.location.back();
  }

  rejectJob(): void {
    this.rejectJobVisible = true;
  }

  watchJob() {
    this.googleAnalyticsService.eventEmitter(
      'watch_job',
      this.googleAnalyticsEventCategory,
      'watch_job',
    );
    this.supplierJobInteractionService.logSingleJobInteraction('WatchJobAttempt', this.job.id);

    this.supplierInboxJobsService.watchJob(this.job.id).then(() => {
      this.supplierJobInteractionService.logSingleJobInteraction('WatchJobSuccess', this.job.id);
      this.eventService.JobScreenEvent.emit({
        event: 'refresh-job',
        data: { jobId: this.job.id },
        sender: 'job-details',
      });
    });
  }

  unwatchJob() {
    this.supplierInboxJobsService.unWatchJob(this.job.id).then(() => {
      this.supplierJobInteractionService.logSingleJobInteraction('UnwatchJobSuccess', this.job.id);
      this.eventService.JobScreenEvent.emit({
        event: 'refresh-job',
        data: { jobId: this.job.id },
        sender: 'job-details',
      });
    });
  }

  syncJobDetailsTab(): void {
    const hasJobDetailsTab = this.tabs.some((tab) => tab.id === 'jobDetails');

    if (this.job.supplierId && !hasJobDetailsTab) {
      const jobDetailsTab: TabType = { label: 'Job Details', id: 'jobDetails', type: 'scroll' };

      this.tabs = [...this.tabs.slice(0, 1), jobDetailsTab, ...this.tabs.slice(1)];
    } else if (!this.job.supplierId && hasJobDetailsTab) {
      this.tabs = this.tabs.filter((tab) => tab.id !== 'jobDetails');
    }
  }

  syncItineraryTab(): void {
    const hasItineraryTab = this.tabs.some((tab) => tab.id === 'routeItinerary');

    if (this.job.routeRecommendationId && !hasItineraryTab) {
      this.tabs = [...this.tabs, { label: 'Itinerary', id: 'routeItinerary', type: 'component' }];
    } else if (!this.job.routeRecommendationId && hasItineraryTab) {
      this.tabs = this.tabs.filter((tab) => tab.id !== 'routeItinerary');
    }
  }

  onTabSelected(tab: TabType): void {
    if (tab.type === 'component') {
      this.activeViewId = tab.id;
    } else {
      this.activeViewId = 'scroll';
    }
  }

  loadRoutesInbox(): void {
    const isAssigned = this.job?.supplierId != null;

    const queryParams: any = {
      ['jobNumberOrVehicleReg']: this.job.jobNumber,
    };

    if (isAssigned) {
      queryParams.extender = 2; //If it's already grabbed then we know we're filtering on Extensions - so default search to this :)
    }

    this.router.navigate(['app/routes-inbox'], {
      queryParams: queryParams,
    });
  }
}
